import { Button, Card, CardBody, CardHeader, Chip, Divider, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Input, Checkbox, useDisclosure, Spinner } from "@nextui-org/react";
import { MagnifyingGlassIcon, LinkIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { useMemo, useState, useCallback } from "react";
import useSWR from "swr";
import ClientDataGrid from "../../../../components/elements/ClientDataGrid";
import RootAdmin from "../../../../components/layouts/RootAdmin";
import { BASE_URL, getFetcher, postFetcher, deleteFetcher } from "../../../../services/api";
import {  
          //calculateRegistrasiRekomendasiProcessingDaysWithTanggalPengajuan, 
          formattedDate, 
          //hasValidRole, isResponseErrorObject 
        } from "../../../../services/helpers";
import { useSelectedCompany } from "../../../../hooks/useSelectedCompany";
import FilterReactSelect from "../../../../components/elements/FilterReactSelect";
import useListCompany from "../../../../hooks/useListCompany";
import useAuth from "../../../../hooks/useAuth";
import toast from "react-hot-toast";
//import RolesAccess from "../../../../enums/roles";

export default function IndexPage() {
  const { 
    user, 
    //roles 
  } = useAuth();
  const [selectedCompany, setSelectedCompany] = useSelectedCompany();
  const query = selectedCompany
    ? `api/registrasi/find-all/search?companyId=${selectedCompany.value}&returnAll=true`
    : `api/registrasi/find-all/search?companyId=${user?.companies?.map((company) => company?.companyId).join(",")}&returnAll=true`;
  const { data, isLoading, mutate } = useSWR(query, getFetcher);
  const { dataCompany, isLoadingCompany } = useListCompany();

  // Modal state for SK selection
  const { isOpen: isSkModalOpen, onOpen: onSkModalOpen, onClose: onSkModalClose } = useDisclosure();
  const [parentSkList, setParentSkList] = useState([]);
  const [selectedSkIds, setSelectedSkIds] = useState([]);
  const [skSearchQuery, setSkSearchQuery] = useState("");
  const [isLoadingParentSk, setIsLoadingParentSk] = useState(false);
  const [isLinkingSk, setIsLinkingSk] = useState(false);
  const [parentCompanyName, setParentCompanyName] = useState("");

  // Confirmation modal state
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();

  // Delete registrasi state
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [blockedReason, setBlockedReason] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleAskDelete = useCallback(
    (row) => {
      setDeleteTarget(row);
      setBlockedReason(null);
      onDeleteOpen();
    },
    [onDeleteOpen]
  );

  const closeDeleteModal = () => {
    onDeleteClose();
    setDeleteTarget(null);
    setBlockedReason(null);
  };

  const handleDeleteRegistrasi = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleting(true);
    try {
      const res = await deleteFetcher("/api/registrasi", deleteTarget.id);
      toast.success(res?.message ?? "Data registrasi berhasil dihapus!");
      closeDeleteModal();
      mutate();
    } catch (error) {
      const payload = error?.response?.data;
      console.log("DELETE registrasi error payload:", error?.response?.status, error?.response?.data);

      if (payload?.code === "REGISTRASI_DIPAKAI_PELAPORAN") {
        setBlockedReason(payload);   // modal TETAP terbuka, isinya berganti
        return;
      }

      toast.error(payload?.message ?? "Gagal menghapus data registrasi");
      closeDeleteModal();
    } finally {
      setIsDeleting(false);
    }
  };

  // Get current company's idCompanySimpel
  const currentCompanyData = useMemo(() => {
    if (!selectedCompany || !dataCompany?.data) return null;
    return dataCompany.data.find((c) => c.id === selectedCompany.value);
  }, [selectedCompany, dataCompany]);

  const hasIdCompanySimpel = currentCompanyData?.idCompanySimpel;
  const hasPerusahaanInduk = currentCompanyData?.idPerusahaanInduk;

  const columns = useMemo(
    () => [
      {
        field: "actions",
        headerName: "",
        width: 60,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button
            isIconOnly
            size="sm"
            radius="full"
            variant="light"
            color="danger"
            aria-label="Hapus registrasi"
            onPress={() => handleAskDelete(params?.row)}
          >
            <XCircleIcon className="size-5 stroke-2" />
          </Button>
        ),
      },
      { field: "index", headerName: "No" },
      { field: "nomor", headerName: "Nomor Permohonan" },
      { field: "companyName", headerName: "Perusahaan", valueGetter: (value, row) => row?.company?.name },
      { field: "nomorSurat", headerName: "Nomor Surat", valueGetter: (value, row) => row?.nomor_surat_formatted },
      {
        field: "subLayanan",
        headerName: "Sub Layanan",
        valueGetter: (value, row) => {
          let subLayanan = row?.sub_layanan;
          if (subLayanan === "119") {
            return "Registrasi B3 (Produsen)";
          } else {
            let kategoriBahan = row?.BahanB3Registrasi?.[0]?.kategori_b3;
            return `Registrasi B3 ${kategoriBahan === "Dapat Dipergunakan" ? "(Baru)" : kategoriBahan === "Terbatas Dipergunakan" ? "(Terbatas)" : ""}`;
          }
        },
      },
      {
        field: "status_izin",
        headerName: "Status Izin",
        valueGetter: (value, row) => {
          if (value === "01") return "Berjangka";
          if (value === "03") return "Seumur Hidup";
          return value || "-";
        },
      },
      { field: "translatedAsalNegara", headerName: "Asal Negara", valueGetter: (value, row) => row?.BahanB3Registrasi[0]?.translatedAsalNegara },
      { field: "pelabuhanBongkar", headerName: "Pelabuhan Bongkar", valueGetter: (value, row) => row?.BahanB3Registrasi[0]?.translatedPelabuhan },
      /*
      { field: "negaraMuat", headerName: "Negara Muat", valueGetter: (value, row) => row?.BahanB3Registrasi[0]?.translatedNegaraMuat },
      { field: "type_registrasi", headerName: "Tipe Registrasi" },
      { field: "tanggal_pengajuan", headerName: "Tanggal Masuk Data", valueGetter: (value, row) => formattedDate(value) },
      {
        field: "lama_proses",
        headerName: "Lama Proses",
        renderCell: (params) => {
          const lamaProses = params.row.lama_proses || 0;
          return lamaProses < 2 ? (
            <Chip color="success" variant="flat" size="sm">
              {lamaProses} Hari
            </Chip>
          ) : (
            <Chip color="danger" variant="flat" size="sm">
              {lamaProses} Hari
            </Chip>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        renderCell: (params) => (
          <Chip size="sm" color="primary" variant="faded" className="capitalize">
            {params.value}
          </Chip>
        ),
      },
      */
      {
        field: "berlaku_dari",
        headerName: "Berlaku Dari",
        renderCell: (params) => formattedDate(params?.value) ?? "-",
      },
      {
        field: "berlaku_sampai",
        headerName: "Berlaku Sampai",
        renderCell: (params) => formattedDate(params?.value) ?? "-",
      },
      {
        field: "file_surat",
        headerName: "File SK",
        renderCell: (params) => {
          if (!params?.row?.file_surat) {
            return "Belum Ada File SK";
          } else {
            const buktiUrl = `${BASE_URL}/${params?.row?.file_surat}`;
            return (
              <a href={buktiUrl} target="_blank" rel="noopener noreferrer" className="text-primary-500 underline">
                Lihat File SK
              </a>
            );
          }
        },
      },
    ],
    [handleAskDelete] 
  );

  /*
  async function onValidateGetPermohonan() {
    try {
      setIsLoadingPtpsp(true);
      if (!selectedCompany) {
        toast.error("Perusahaan harus dipilih!");
        setIsLoadingPtpsp(false);
        return;
      }
      let userCom;
      if (user?.companies?.length === 0 && !hasValidRole(roles, [RolesAccess.SUPER_ADMIN])) {
        toast.error("Anda tidak memiliki perusahaan terdaftar!");
        setIsLoadingPtpsp(false);
        return;
      }
      userCom = dataCompany?.data?.filter((item) => item.id === selectedCompany.value)?.map((item) => item?.kode_customer ?? "");

      const data = {
        kode_kustomer: userCom ?? "",
        status: "SUKSES",
        subLayanan: ["1", "2"],
      };
      await postFetcher(`/api/registrasi/get-from-ptsp-user`, data);
      mutate();
      toast.success("Berhasil Update List Permohonan Registrasi Dari PTSP!");
      setIsLoadingPtpsp(false);
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        isResponseErrorObject(error.response.data.message)
          ? Object.entries(error.response.data.message).forEach(([key, value]) => {
              toast.error(value);
            })
          : toast.error(error.response.data.message);
      } else {
        console.log(error);
        // In case error.response or error.response.data is undefined
        toast.error("An unexpected error occurred.");
      }
      mutate();
      setIsLoadingPtpsp(false);
    }
  }
  */

  // Fetch parent company SK registrasi
  const fetchParentSkList = useCallback(async () => {
    if (!selectedCompany) return;

    setIsLoadingParentSk(true);
    try {
      const response = await getFetcher(`/api/registrasi/parent-company-sk/${selectedCompany.value}`);
      setParentSkList(response.registrasi || []);
      setParentCompanyName(response.parentCompany?.name || "");
    } catch (error) {
      console.error("Error fetching parent SK:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Gagal mengambil daftar SK dari perusahaan induk");
      }
      setParentSkList([]);
    } finally {
      setIsLoadingParentSk(false);
    }
  }, [selectedCompany]);

  // Open SK modal
  const handleOpenSkModal = async () => {
    if (!selectedCompany) {
      toast.error("Pilih perusahaan terlebih dahulu!");
      return;
    }
    if (!hasIdCompanySimpel) {
      toast.error("Perusahaan ini bukan perusahaan SIMPEL!");
      return;
    }
    if (!hasPerusahaanInduk) {
      toast.error("Perusahaan ini tidak memiliki perusahaan induk!");
      return;
    }

    setSelectedSkIds([]);
    setSkSearchQuery("");
    await fetchParentSkList();
    onSkModalOpen();
  };

  // Filter SK list based on search query (client-side)
  const filteredSkList = useMemo(() => {
    if (!skSearchQuery.trim()) return parentSkList;

    const searchLower = skSearchQuery.toLowerCase();
    return parentSkList.filter((sk) => {
      const nomorSurat = (sk.nomor_surat_formatted || "").toLowerCase();
      const nomor = (sk.nomor || "").toLowerCase();
      const namaBahan = (sk.BahanB3Registrasi?.[0]?.nama_bahan || "").toLowerCase();
      const namaDagang = (sk.BahanB3Registrasi?.[0]?.nama_dagang || "").toLowerCase();

      return nomorSurat.includes(searchLower) || nomor.includes(searchLower) || namaBahan.includes(searchLower) || namaDagang.includes(searchLower);
    });
  }, [parentSkList, skSearchQuery]);

  // Handle SK checkbox toggle
  const handleSkToggle = (skId, sudahDipegangPerusahaanIni) => {
    // Hanya SK yang sudah dipegang perusahaan ini yang tidak bisa dipilih lagi.
    // SK yang dipegang perusahaan anak LAIN tetap boleh dipilih - satu SK induk
    // memang boleh dipegang beberapa anak sekaligus.
    if (sudahDipegangPerusahaanIni) return;

    setSelectedSkIds((prev) => {
      if (prev.includes(skId)) {
        return prev.filter((id) => id !== skId);
      } else {
        return [...prev, skId];
      }
    });
  };

  // Handle confirm link SK
  const handleConfirmLinkSk = async () => {
    if (selectedSkIds.length === 0) {
      toast.error("Pilih minimal satu SK registrasi!");
      return;
    }
    onConfirmOpen();
  };

  // Actually link the SK
  const handleLinkSk = async () => {
    setIsLinkingSk(true);
    try {
      await postFetcher("/api/registrasi/link-sk-to-company", {
        registrasiIds: selectedSkIds,
        companyId: selectedCompany.value,
      });
      toast.success("Berhasil menambahkan SK registrasi ke perusahaan!");
      onConfirmClose();
      onSkModalClose();
      mutate(); // Refresh the main list
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Gagal menambahkan SK registrasi");
      }
    } finally {
      setIsLinkingSk(false);
    }
  };

  // Get selected SK details for confirmation
  const selectedSkDetails = useMemo(() => {
    return parentSkList.filter((sk) => selectedSkIds.includes(sk.id));
  }, [parentSkList, selectedSkIds]);

  return (
    <RootAdmin>
      <Card className="w-full mt-3" radius="sm">
        <CardHeader>
          <p className="text-md">Daftar Registrasi</p>
        </CardHeader>
        <Divider />
        <CardBody className="w-full h-[550px] p-5">
          <div className="w-full flex justify-between pb-3">
            <div className="w-1/4">
              <FilterReactSelect defaultValue={selectedCompany} isLoading={isLoadingCompany} options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))} setValue={setSelectedCompany} placeholder="Pilih Perusahaan" />
            </div>
            {/* Button to update SK list for SIMPEL companies */}
            {hasIdCompanySimpel && hasPerusahaanInduk && (
              <div className="flex items-center">
                <Button size="sm" color="secondary" startContent={<LinkIcon className="size-4 stroke-2" />} onClick={handleOpenSkModal}>
                  Update Daftar SK
                </Button>
              </div>
            )}
          </div>
          <ClientDataGrid columns={columns} isLoading={isLoading} rows={data?.registrasi} />
        </CardBody>
      </Card>

      {/* Modal for SK Selection */}
      <Modal isOpen={isSkModalOpen} onClose={onSkModalClose} size="4xl" scrollBehavior="inside">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <p>Pilih SK Registrasi dari Perusahaan Induk</p>
            {parentCompanyName && <p className="text-sm font-normal text-ink-500">Perusahaan Induk: {parentCompanyName}</p>}
          </ModalHeader>
          <ModalBody>
            {/* Search input */}
            <div className="mb-4">
              <Input
                placeholder="Cari nomor surat, nomor registrasi, atau nama bahan..."
                value={skSearchQuery}
                onChange={(e) => setSkSearchQuery(e.target.value)}
                startContent={<MagnifyingGlassIcon className="size-4 text-ink-400" />}
                size="sm"
                isClearable
                onClear={() => setSkSearchQuery("")}
              />
            </div>

            {/* SK List */}
            {isLoadingParentSk ? (
              <div className="flex justify-center items-center py-10">
                <Spinner size="lg" />
              </div>
            ) : filteredSkList.length === 0 ? (
              <div className="text-center py-10 text-ink-500">{parentSkList.length === 0 ? "Tidak ada SK registrasi ditemukan dari perusahaan induk" : "Tidak ada hasil pencarian"}</div>
            ) : (
              <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-3 sticky top-0 z-10">
                    <tr>
                      <th className="p-3 text-left w-12 bg-surface-3"></th>
                      <th className="p-3 text-left bg-surface-3">Nomor Surat</th>
                      <th className="p-3 text-left bg-surface-3">Nomor Registrasi</th>
                      <th className="p-3 text-left bg-surface-3">Nama Bahan</th>
                      <th className="p-3 text-left bg-surface-3">Status</th>
                      <th className="p-3 text-left bg-surface-3">Keterangan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSkList.map((sk, index) => (
                      <tr key={sk.id} className={`border-t ${sk.terhubungKePerusahaanIni ? "bg-surface-2 opacity-60" : "hover:bg-surface-2 cursor-pointer"}`} onClick={() => !sk.terhubungKePerusahaanIni && handleSkToggle(sk.id, sk.terhubungKePerusahaanIni)}>
                        <td className="p-3">
                          <Checkbox isSelected={selectedSkIds.includes(sk.id)} isDisabled={sk.terhubungKePerusahaanIni} onChange={() => handleSkToggle(sk.id, sk.terhubungKePerusahaanIni)} />
                        </td>
                        <td className="p-3">{sk.nomor_surat_formatted || "-"}</td>
                        <td className="p-3">{sk.nomor || "-"}</td>
                        <td className="p-3">{sk.BahanB3Registrasi?.[0]?.nama_dagang || sk.BahanB3Registrasi?.[0]?.nama_bahan || "-"}</td>
                        <td className="p-3">
                          <Chip size="sm" color="primary" variant="flat" className="capitalize">
                            {sk.status || "-"}
                          </Chip>
                        </td>
                        <td className="p-3">
                          {/*
                            Satu SK induk boleh dipegang beberapa perusahaan anak
                            sekaligus. Baris hanya dimatikan bila perusahaan yang
                            sedang dibuka SUDAH memegangnya; kalau yang memegang
                            perusahaan lain, SK tetap boleh dipilih dan namanya
                            ditulis sebagai keterangan.
                          */}
                          {sk.terhubungKePerusahaanIni ? (
                            <Chip size="sm" color="success" variant="flat">
                              Sudah dipegang perusahaan ini
                            </Chip>
                          ) : sk.perusahaanPemegang?.length ? (
                            <span className="text-xs text-ink-500">
                              Juga dipegang: {sk.perusahaanPemegang.map((pt) => pt.name).join(", ")}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onPress={onSkModalClose}>
              Batal
            </Button>
            <Button color="primary" onPress={handleConfirmLinkSk} isDisabled={selectedSkIds.length === 0}>
              Tambahkan SK Terpilih ({selectedSkIds.length})
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Confirmation Modal */}
      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} size="lg">
        <ModalContent>
          <ModalHeader>Konfirmasi</ModalHeader>
          <ModalBody>
            <p className="mb-4">Apakah Anda yakin akan menambahkan SK berikut ke akun perusahaan Anda?</p>
            <div className="border rounded-lg p-3 bg-surface-2 max-h-60 overflow-y-auto">
              <ul className="list-disc list-inside space-y-2">
                {selectedSkDetails.map((sk) => (
                  <li key={sk.id} className="text-sm">
                    <span className="font-medium">{sk.nomor_surat_formatted || sk.nomor}</span>
                    {sk.BahanB3Registrasi?.[0]?.nama_dagang && <span className="text-ink-500"> - {sk.BahanB3Registrasi[0].nama_dagang}</span>}
                  </li>
                ))}
              </ul>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onPress={onConfirmClose} isDisabled={isLinkingSk}>
              Batal
            </Button>
            <Button color="primary" onPress={handleLinkSk} isLoading={isLinkingSk}>
              Ya, Tambahkan
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Konfirmasi Delete SK */}
      <Modal isOpen={isDeleteOpen} onClose={closeDeleteModal} size="lg">
        <ModalContent>
          <ModalHeader>
            {blockedReason ? "Registrasi Tidak Dapat Dihapus" : "Hapus Data Registrasi"}
          </ModalHeader>
          <ModalBody>
            <div className="border rounded-lg p-3 bg-surface-2 text-sm">
              <p><span className="font-medium">Nomor Registrasi:</span> {deleteTarget?.nomor || "-"}</p>
              <p><span className="font-medium">Nomor Surat:</span> {deleteTarget?.nomor_surat_formatted || "-"}</p>
              <p><span className="font-medium">Perusahaan:</span> {deleteTarget?.company?.name || "-"}</p>
            </div>

            {blockedReason ? (
              <div className="border border-danger-200 rounded-lg p-3 bg-danger-50">
                <p className="text-sm mb-2">
                  Registrasi ini masih dipakai pada{" "}
                  <span className="font-medium">{blockedReason.totalPelaporan}</span> baris data
                  pelaporan{" "}
                  {blockedReason.jenisRegistrasi === "produsen" ? "produksi B3" : "pembelian B3"}:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm mb-3">
                  {blockedReason.dampakPelaporan?.map((item) => (
                    <li key={item.key}>
                      {item.label} — <span className="font-medium">{item.jumlah} baris</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm font-medium text-danger-600">
                  Silahkan hapus data pelaporannya terlebih dahulu, sebelum menghapus registrasi ini.
                </p>
              </div>
            ) : (
              <p className="text-sm text-danger-500">Tindakan ini tidak dapat dibatalkan.</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="default" variant="light" onPress={closeDeleteModal} isDisabled={isDeleting}>
              {blockedReason ? "Tutup" : "Batal"}
            </Button>
            {!blockedReason && (
              <Button color="danger" onPress={handleDeleteRegistrasi} isLoading={isDeleting}>
                Ya, Hapus
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </RootAdmin>
  );
}
